/**
 * The bout's qinggong, knock, armour, rage and lock-routine sounds (the ones sfx/duelWatch.ts plays from the duel systems'
 * state, since most of them raise no WorldEvent). Self-made synth, no samples. Wuxia register: cloth, qi, steel, a
 * bell on the big moments. Levels sit with sounds/movement.ts and sounds/combat.ts (peaks about 0.3).
 */
import { STEEL } from '../kit';
import { registerSfx } from '../registry';
import { bowl, chime, gong, note, ROOT_D, whoosh } from '../voices';

// ───────── steps (move) ─────────

registerSfx('duel.step.back', { label: 'ก้าวถอยหลัง (ประลอง)', cat: 'move', gain: 1.8, vary: 0.05, gap: 0.08 }, (k) => {
  // Quick, falling: cloth snapping away, a scuff of the heel.
  whoosh(k, { dur: 0.2, f0: 2200, f1: 900, f2: 450, vol: 0.2, q: 1.4, peak: 0.1 });
  k.noise({ dur: 0.05, vol: 0.12, attack: 0.002, type: 'highpass', f: 3800 });
  k.noise({ color: 'brown', at: 0.02, dur: 0.1, vol: 0.14, type: 'lowpass', f: 600 });
});

registerSfx('duel.step.side', { label: 'ก้าวเฉียงรอบคู่ต่อสู้', cat: 'move', gain: 1.5, vary: 0.05, gap: 0.08 }, (k) => {
  // A curve: the band rises and falls, a flutter of sleeve on the way round.
  k.noise({ dur: 0.26, vol: 0.2, attack: 0.1, f: 700, f1: 1900, f2: 800, sweep: 0.14, q: 2, trem: [24, 0.35] });
  k.noise({ at: 0.02, dur: 0.18, vol: 0.06, color: 'pink', attack: 0.06, type: 'highpass', f: 3000 });
  k.thump({ f0: 150, f1: 80, dur: 0.07, vol: 0.12 });
});

registerSfx('duel.step.fwd', { label: 'ก้าวเข้าประชิด', cat: 'move', gain: 0.33, vary: 0.05, gap: 0.08 }, (k) => {
  // Heavier, rising: a lunge in, the planted foot at the end.
  whoosh(k, { dur: 0.28, f0: 350, f1: 2400, f2: 1100, vol: 0.26, q: 1.6, peak: 0.55 });
  k.thump({ f0: 190, f1: 70, at: 0.2, dur: 0.1, vol: 0.28 });
  k.noise({ color: 'brown', at: 0.2, dur: 0.1, vol: 0.14, type: 'lowpass', f: 700 });
});

registerSfx('duel.step.air', { label: 'พุ่งกลางอากาศ', cat: 'move', vary: 0.05, gap: 0.08 }, (k) => {
  // Thin and airy, a qi flick: no ground in it.
  whoosh(k, { dur: 0.24, f0: 1200, f1: 4200, f2: 1800, vol: 0.2, q: 2.2, peak: 0.3 });
  chime(k, { f: note(ROOT_D, 7) * 4, at: 0.02, vol: 0.06, dur: 0.3 });
  k.noise({ dur: 0.05, vol: 0.08, type: 'highpass', f: 5000 });
});

registerSfx('duel.escape', { label: 'สลัดหลุด', cat: 'combat', gain: 0.8, vary: 0.04, gap: 0.2, preview: 0.8 }, (k) => {
  // A grip thrown off: a tearing cloth snap, a bright "tsing" of qi breaking, a hard step back.
  k.noise({ dur: 0.06, vol: 0.34, attack: 0.001, type: 'highpass', f: 3500 });
  k.metal({ f: 1500, dur: 0.35, vol: 0.1, partials: STEEL });
  whoosh(k, { at: 0.02, dur: 0.22, f0: 2600, f1: 800, f2: 400, vol: 0.2, q: 1.5, peak: 0.1 });
  chime(k, { f: note(ROOT_D, 9) * 4, at: 0.04, vol: 0.1, dur: 0.4 });
  k.thump({ f0: 200, f1: 70, at: 0.12, dur: 0.1, vol: 0.2 });
});

// ───────── dive ─────────

registerSfx('duel.dive', { label: 'โฉบลงมา (ท่าโฉบ)', cat: 'move', gain: 0.6, vary: 0.04, gap: 0.2 }, (k) => {
  // The 0.3 s descent: a falling whistle through torn air, a blade's edge in it.
  k.tone({ type: 'sine', f: 1900, f1: 420, dur: 0.3, vol: 0.07, attack: 0.01, linear: true });
  whoosh(k, { dur: 0.3, f0: 800, f1: 3800, f2: 3200, vol: 0.26, q: 2, peak: 0.85 });
  k.noise({ color: 'pink', dur: 0.3, vol: 0.06, attack: 0.25, type: 'highpass', f: 2500 });
});

registerSfx('duel.dive.land', { label: 'โฉบถึงพื้น', cat: 'combat', vary: 0.04, gap: 0.2 }, (k) => {
  // A crack with a thud under it and a ring of steel; dust after.
  k.noise({ dur: 0.03, vol: 0.36, attack: 0.001, type: 'highpass', f: 3000 });
  k.thump({ f0: 140, f1: 45, dur: 0.18, vol: 0.34 });
  k.metal({ f: 1900, dur: 0.4, vol: 0.09, partials: STEEL });
  k.noise({ color: 'brown', at: 0.03, dur: 0.22, vol: 0.18, type: 'lowpass', f: 900, f1: 250 });
});

// ───────── slide ─────────

registerSfx('duel.slide', { label: 'สไลด์ (ลื่นไถลต่ำ)', cat: 'move', gain: 1.6, vary: 0.05, gap: 0.15, preview: 0.7 }, (k) => {
  // Cloth and shin dragging over the ground: a low gritty band that thins as the speed falls.
  k.noise({ color: 'brown', dur: 0.5, vol: 0.2, attack: 0.04, type: 'bandpass', f: 900, f1: 350, q: 0.9 });
  k.noise({ dur: 0.4, vol: 0.12, attack: 0.03, f: 3200, f1: 1400, q: 1.2, trem: [38, 0.5] });
  k.thump({ f0: 130, f1: 70, dur: 0.1, vol: 0.2 });
});

registerSfx('duel.slide.stop', { label: 'หยุดสไลด์', cat: 'move', gain: 0.45, vary: 0.05, gap: 0.15 }, (k) => {
  // A last scrape, a boot planted, cloth settling.
  k.noise({ dur: 0.14, vol: 0.18, attack: 0.01, f: 2200, f1: 600, q: 1.5 });
  k.thump({ f0: 160, f1: 60, at: 0.06, dur: 0.1, vol: 0.24 });
  k.noise({ at: 0.1, dur: 0.18, vol: 0.06, color: 'pink', attack: 0.05, type: 'highpass', f: 4000 });
});

registerSfx('duel.slide.strike', { label: 'ฟันจากสไลด์', cat: 'combat', gain: 0.45, vary: 0.04, gap: 0.2 }, (k) => {
  // Rising out of the slide: a blade whoosh from low, a hard ring.
  whoosh(k, { dur: 0.26, f0: 400, f1: 3600, f2: 1600, vol: 0.3, q: 1.8, peak: 0.45 });
  k.metal({ f: 1650, at: 0.1, dur: 0.45, vol: 0.12, partials: STEEL });
  k.thump({ f0: 220, f1: 90, at: 0.1, dur: 0.1, vol: 0.2 });
});

// ───────── knock ─────────

registerSfx('duel.knock.fling', { label: 'ถูกซัดกระเด็น', cat: 'combat', gain: 1.4, vary: 0.04, gap: 0.3, preview: 0.7 }, (k) => {
  // The big blow's follow-through: a long rushing sweep, cloth flapping, a breathy deep boom.
  k.thump({ f0: 240, f1: 55, dur: 0.22, vol: 0.34 });
  whoosh(k, { dur: 0.5, f0: 500, f1: 2800, f2: 700, vol: 0.28, q: 1.2, peak: 0.25, color: 'pink' });
  k.noise({ at: 0.05, dur: 0.4, vol: 0.1, f: 1200, f1: 500, q: 0.8, trem: [30, 0.6] });
});

registerSfx('duel.knock.trip', { label: 'สะดุดล้ม', cat: 'combat', gain: 0.85, vary: 0.05, gap: 0.3 }, (k) => {
  // A swept leg: a quick scrape and a stumble of cloth.
  k.noise({ dur: 0.16, vol: 0.2, attack: 0.02, f: 2400, f1: 700, q: 1.4 });
  k.thump({ f0: 180, f1: 80, at: 0.1, dur: 0.1, vol: 0.2 });
});

registerSfx('duel.knock.down', { label: 'ล้มลงพื้น', cat: 'combat', gain: 0.9, vary: 0.05, gap: 0.3, preview: 0.7 }, (k) => {
  // A body on packed earth: a deep thud, the breath going out, dust and cloth settling.
  k.thump({ f0: 120, f1: 38, dur: 0.24, vol: 0.4 });
  k.noise({ color: 'brown', dur: 0.2, vol: 0.28, type: 'lowpass', f: 800, f1: 180 });
  k.noise({ at: 0.04, dur: 0.35, vol: 0.09, color: 'pink', attack: 0.08, f: 1800, f1: 900, q: 0.7 });
  k.click({ f: 2800, at: 0.02, vol: 0.1, q: 3 });
});

registerSfx('duel.getup.rise', { label: 'ลุกขึ้น', cat: 'move', gain: 0.55, vary: 0.05, gap: 0.3 }, (k) => {
  // Cloth rustle rising, a hand pushing off, a settled step.
  k.noise({ dur: 0.34, vol: 0.14, attack: 0.1, f: 900, f1: 2600, q: 1, trem: [18, 0.4] });
  k.thump({ f0: 140, f1: 70, at: 0.3, dur: 0.08, vol: 0.18 });
  k.tone({ type: 'triangle', f: note(ROOT_D, 4) * 2, at: 0.3, dur: 0.18, vol: 0.04, attack: 0.01 });
});

registerSfx('duel.getup.roll', { label: 'กลิ้งลุก', cat: 'move', gain: 0.4, vary: 0.05, gap: 0.3, preview: 0.7 }, (k) => {
  // Three turns over the ground: scuffs in a quick triple, then a landing on the feet.
  for (let i = 0; i < 3; i++) {
    k.noise({ at: i * 0.1, dur: 0.12, vol: 0.18, attack: 0.02, f: 1400 + i * 300, f1: 500, q: 1.1 });
    k.thump({ f0: 130, f1: 70, at: i * 0.1, dur: 0.06, vol: 0.12 });
  }
  k.thump({ f0: 170, f1: 60, at: 0.32, dur: 0.1, vol: 0.24 });
});

registerSfx('duel.getup.attack', { label: 'ลุกพร้อมฟัน', cat: 'combat', gain: 0.7, vary: 0.04, gap: 0.3 }, (k) => {
  // Up with a cut: a rising whoosh and a small steel flash.
  whoosh(k, { dur: 0.24, f0: 500, f1: 3600, f2: 1600, vol: 0.26, q: 1.6, peak: 0.5 });
  k.metal({ f: 2000, at: 0.1, dur: 0.3, vol: 0.09, partials: STEEL });
  k.thump({ f0: 200, f1: 80, at: 0.08, dur: 0.08, vol: 0.16 });
});

// ───────── armour (the soaked blow, the break) ─────────

registerSfx('duel.armor.red', { label: 'เกราะแดง รับดาบ', cat: 'combat', vary: 0.04, gap: 0.08 }, (k) => {
  // Red: a full iron body. The blow dulls on a deep, short bronze note; no give.
  k.noise({ dur: 0.02, vol: 0.3, attack: 0.001, type: 'highpass', f: 2800 });
  k.metal({ f: 420, dur: 0.45, vol: 0.2, partials: STEEL });
  k.thump({ f0: 190, f1: 70, dur: 0.14, vol: 0.28 });
  k.tone({ type: 'sine', f: 110, dur: 0.2, vol: 0.1, attack: 0.002 });
});

registerSfx('duel.armor.yellow', { label: 'เกราะเหลือง รับดาบ', cat: 'combat', vary: 0.04, gap: 0.08 }, (k) => {
  // Yellow: a thinner shell. A brighter ring, a bit of a crackle: it is giving.
  k.noise({ dur: 0.02, vol: 0.28, attack: 0.001, type: 'highpass', f: 3800 });
  k.metal({ f: 1050, dur: 0.35, vol: 0.14, partials: STEEL });
  k.thump({ f0: 230, f1: 100, dur: 0.1, vol: 0.2 });
  k.noise({ at: 0.02, dur: 0.1, vol: 0.06, f: 5200, q: 1.5, trem: [60, 0.8] });
});

registerSfx('duel.armor.break', { label: 'เกราะเหลืองแตก', cat: 'combat', vary: 0.03, gap: 0.3, preview: 1 }, (k) => {
  // The shell shatters: a hard crack, shards raining up high, a low gong as the qi collapses.
  k.noise({ dur: 0.03, vol: 0.4, attack: 0.001, type: 'highpass', f: 2500 });
  k.thump({ f0: 260, f1: 60, dur: 0.2, vol: 0.34 });
  for (let i = 0; i < 6; i++)
    k.click({ f: 3200 + i * 650 + k.range(-200, 200), at: 0.02 + i * 0.03 + k.range(0, 0.02), vol: 0.1, q: 5 });
  k.metal({ f: 1500, at: 0.02, dur: 0.5, vol: 0.12, partials: STEEL });
  gong(k, { f: 150, at: 0.04, dur: 0.8, vol: 0.14 });
});

// ───────── rage ─────────

registerSfx('duel.rageFull', { label: 'ความโกรธเต็ม (ท่าใหญ่พร้อม)', cat: 'combat', gain: 0.6, vary: 0, gap: 1, preview: 1.4 }, (k) => {
  // The bar fills: a low qi swell rising a fifth, a bowl, a bright pentatonic ripple over it. Plays once.
  k.fm({ f: 98, ratio: 2, index: 1.6, dur: 0.9, vol: 0.12, attack: 0.4 });
  k.tone({ type: 'sine', f: 110, f1: 165, dur: 0.8, vol: 0.12, attack: 0.35, linear: true });
  bowl(k, { f: note(ROOT_D, 0), at: 0.4, dur: 1.2, vol: 0.14 });
  [0, 2, 4, 7].forEach((d, i) => chime(k, { f: note(ROOT_D, d) * 4, at: 0.45 + i * 0.07, vol: 0.09, dur: 0.6 }));
  k.noise({ dur: 0.6, vol: 0.1, attack: 0.5, f: 800, f1: 4200, q: 2 });
});

// ───────── lock routines (data/lockRoutines.ts: each beat names its sound key; played as `duel.lock.<key>`) ─────────

registerSfx('duel.lock.bladeBind', { label: 'ท่ายาว กระบี่: ประกบคม', cat: 'combat', vary: 0.03, gap: 0.2, preview: 0.9 }, (k) => {
  // Two blades meeting and held: a ringing contact, a rising scrape of steel on steel.
  k.metal({ f: 1700, dur: 0.7, vol: 0.14, partials: STEEL });
  k.noise({ dur: 0.45, vol: 0.12, attack: 0.1, type: 'bandpass', f: 2600, f1: 5200, q: 6 });
  k.thump({ f0: 240, f1: 120, dur: 0.1, vol: 0.18 });
});
registerSfx('duel.lock.swordCutLight', { label: 'ท่ายาว กระบี่: ฟันเบา', cat: 'combat', vary: 0.07, gap: 0.2 }, (k) => {
  whoosh(k, { dur: 0.2, f0: 700, f1: 4200, f2: 1800, vol: 0.24, q: 1.8, peak: 0.4 });
  k.metal({ f: 2100, at: 0.07, dur: 0.25, vol: 0.08, partials: STEEL });
});
registerSfx('duel.lock.swordThrust3', { label: 'ท่ายาว กระบี่: แทงสามจุด', cat: 'combat', gain: 0.8, vary: 0.03, gap: 0.3, preview: 0.7 }, (k) => {
  for (let i = 0; i < 3; i++) {
    k.noise({ at: i * 0.13, dur: 0.07, vol: 0.2, attack: 0.005, f: 2800 + i * 500, f1: 5200, q: 2 });
    k.click({ f: 4200 + i * 400, at: i * 0.13 + 0.05, vol: 0.2, q: 5 });
    k.thump({ f0: 250, f1: 110, at: i * 0.13 + 0.05, dur: 0.06, vol: 0.14 });
  }
  k.metal({ f: 2300, at: 0.3, dur: 0.4, vol: 0.08, partials: STEEL });
});
registerSfx('duel.lock.qiGather', { label: 'ท่ายาว: รวมลมปราณ', cat: 'combat', gain: 0.7, vary: 0, gap: 0.5, preview: 1 }, (k) => {
  // A breath drawn in: filtered air sweeping up, a low hum swelling, a high shimmer at the top.
  k.noise({ dur: 0.55, vol: 0.22, attack: 0.5, f: 300, f1: 3600, q: 2.5, linear: true });
  k.tone({ type: 'sine', f: 98, f1: 196, dur: 0.55, vol: 0.14, attack: 0.45, linear: true });
  k.tone({ type: 'triangle', f: note(ROOT_D, 4) * 2, at: 0.3, dur: 0.3, vol: 0.05, attack: 0.25, vib: [6, 0.01] });
  chime(k, { f: note(ROOT_D, 9) * 4, at: 0.5, vol: 0.07, dur: 0.5 });
});
registerSfx('duel.lock.swordFinisher', { label: 'ท่ายาว กระบี่: ฟันอำลา', cat: 'combat', gain: 0.5, vary: 0.02, gap: 0.5, preview: 1.4 }, (k) => {
  // The parting cut: a wide slash, steel ringing out, a boom under it, a bell for the end of the phrase.
  whoosh(k, { dur: 0.3, f0: 500, f1: 4800, f2: 1200, vol: 0.34, q: 1.6, peak: 0.35 });
  k.noise({ at: 0.12, dur: 0.03, vol: 0.4, attack: 0.001, type: 'highpass', f: 3000 });
  k.metal({ f: 1650, at: 0.12, dur: 1.0, vol: 0.18, partials: STEEL });
  k.thump({ f0: 200, f1: 45, at: 0.12, dur: 0.25, vol: 0.38 });
  gong(k, { f: 140, at: 0.14, dur: 1.1, vol: 0.16 });
  chime(k, { f: note(ROOT_D, 7) * 4, at: 0.2, vol: 0.1, dur: 0.8 });
});
registerSfx('duel.lock.palmStick', { label: 'ท่ายาว ฝ่ามือ: มือเกาะแขน', cat: 'combat', gain: 1.3, vary: 0.04, gap: 0.2 }, (k) => {
  // A palm laid on the sleeve and held: a soft slap, a low sticky pluck, a thread of cloth.
  k.noise({ dur: 0.05, vol: 0.2, attack: 0.002, f: 1800, q: 1.2 });
  k.thump({ f0: 200, f1: 90, dur: 0.1, vol: 0.2 });
  k.pluck({ f: note(ROOT_D, 0) / 2, at: 0.04, dur: 0.6, vol: 0.12, bright: 0.25 });
  k.noise({ at: 0.06, dur: 0.3, vol: 0.05, attack: 0.1, f: 2400, q: 1, trem: [14, 0.5] });
});
registerSfx('duel.lock.clothWhirl', { label: 'ท่ายาว ฝ่ามือ: ดึงวน', cat: 'combat', vary: 0.04, gap: 0.3, preview: 0.9 }, (k) => {
  // Two sleeves drawing a circle: a long breathy sweep with a flutter, panned by the engine's placement.
  k.noise({ dur: 0.7, vol: 0.2, attack: 0.3, f: 600, f1: 2200, f2: 700, sweep: 0.35, q: 1.3, trem: [11, 0.5] });
  k.noise({ at: 0.1, dur: 0.5, vol: 0.06, color: 'pink', attack: 0.2, type: 'highpass', f: 3500, trem: [22, 0.5] });
  k.tone({ type: 'sine', f: 130, f1: 170, dur: 0.6, vol: 0.06, attack: 0.3 });
});
registerSfx('duel.lock.palmTap', { label: 'ท่ายาว ฝ่ามือ: ตบเบา', cat: 'combat', gain: 1.8, vary: 0.06, gap: 0.2 }, (k) => {
  k.noise({ dur: 0.04, vol: 0.22, attack: 0.002, f: 1500, q: 1 });
  k.thump({ f0: 190, f1: 80, dur: 0.1, vol: 0.28 });
  k.noise({ at: 0.03, dur: 0.14, vol: 0.06, attack: 0.04, f: 2400, f1: 1200, q: 1 });
});
registerSfx('duel.lock.palmPress', { label: 'ท่ายาว ฝ่ามือ: กดลงพื้น', cat: 'combat', gain: 0.9, vary: 0.04, gap: 0.3, preview: 0.8 }, (k) => {
  // Pressed into the ground: a slow, heavy sink, earth groaning, a low ring-out.
  k.thump({ f0: 110, f1: 40, dur: 0.35, vol: 0.36 });
  k.noise({ color: 'brown', dur: 0.35, vol: 0.22, attack: 0.05, type: 'lowpass', f: 500, f1: 120 });
  bowl(k, { f: note(ROOT_D, 0) / 2, at: 0.05, dur: 0.9, vol: 0.1 });
});
registerSfx('duel.lock.palmTap2', { label: 'ท่ายาว ฝ่ามือ: ตบสองที', cat: 'combat', gain: 0.6, vary: 0.05, gap: 0.3 }, (k) => {
  for (let i = 0; i < 2; i++) {
    k.noise({ at: i * 0.12, dur: 0.04, vol: 0.22, attack: 0.002, f: 1500 + i * 400, q: 1 });
    k.thump({ f0: 200 + i * 30, f1: 80, at: i * 0.12, dur: 0.1, vol: 0.28 });
  }
  chime(k, { f: note(ROOT_D, 4) * 4, at: 0.13, vol: 0.05, dur: 0.4 });
});
registerSfx('duel.lock.palmFinisher', { label: 'ท่ายาว ฝ่ามือ: ผลักคู่', cat: 'combat', gain: 0.75, vary: 0.02, gap: 0.5, preview: 1.4 }, (k) => {
  // The double push: air compressed and released, a boom that is felt, a ring of qi afterwards.
  k.noise({ dur: 0.08, vol: 0.32, attack: 0.001, type: 'highpass', f: 1800 });
  k.thump({ f0: 170, f1: 36, dur: 0.4, vol: 0.44 });
  whoosh(k, { at: 0.02, dur: 0.5, f0: 300, f1: 3000, f2: 500, vol: 0.28, q: 1.1, peak: 0.2, color: 'pink' });
  gong(k, { f: 118, at: 0.03, dur: 1.2, vol: 0.18 });
  chime(k, { f: note(ROOT_D, 9) * 4, at: 0.25, vol: 0.08, dur: 0.8 });
});

registerSfx('duel.lock.escape', { label: 'ท่ายาว: หลุดจากล็อก (F+Q)', cat: 'combat', vary: 0.03, gap: 0.3, preview: 1 }, (k) => {
  // The catch broken: a tight snap, a bright steel "tsing" and a shock of air pushing both apart.
  k.noise({ dur: 0.04, vol: 0.4, attack: 0.001, type: 'highpass', f: 3000 });
  k.metal({ f: 1900, dur: 0.5, vol: 0.14, partials: STEEL });
  whoosh(k, { at: 0.02, dur: 0.3, f0: 3200, f1: 700, f2: 300, vol: 0.24, q: 1.3, peak: 0.1 });
  k.thump({ f0: 220, f1: 60, at: 0.03, dur: 0.18, vol: 0.3 });
  chime(k, { f: note(ROOT_D, 7) * 4, at: 0.08, vol: 0.1, dur: 0.6 });
});
